import React, { useState, useMemo, useCallback } from 'react'
import { Button, Empty, Skeleton, Tabs, Tooltip } from 'antd'

import { SectionHeader } from '../SectionHeader'
import { Request } from './Request.jsx'
import { Prescription } from './Prescription.jsx'

import { useCaseViewContext } from '../../context'
import { useNavigation } from '../../context/NavigationContext'
import { useCaseHistory } from '../../hooks/useCaseHistory'
import { usePrescribedTreatments } from '../../hooks/usePrescribedTreatments'
import { useDispenseUnits } from '../../hooks/useDispenseUnits'

import { Container } from "../../Styles"
import { ShowErrorsSwitch } from './Prescription.styles'

export const History = () => {
    const [prescriptions, setPrescriptions] = useState({
        visible: [],
        withErrors: false,
    })

    const { caseDetails, activeRequest, apiClasses, apiType } = useCaseViewContext()
    const { navigateToCase } = useNavigation()
    const patientId = caseDetails?.patientId
    const currentCaseId = caseDetails?._id || caseDetails?.caseId

    const { caseHistory, loading, error } = useCaseHistory(patientId, apiClasses, currentCaseId)
    const { getDispenseUnitById } = useDispenseUnits({ apiClasses, apiType: apiType || 'physician' })

    const {
        prescriptions: allPrescriptions = [],
        loading: prescriptionsLoading,
        error: prescriptionsError,
        fetchPrescriptionHistory,
    } = usePrescribedTreatments({
        apiType,
        source: 'history',
        caseDetails,
        activeRequest,
        apiClasses: apiClasses
    })

    const handleNavigateToCase = useCallback((caseId, requestId) => {
        navigateToCase(caseId, requestId, apiType);
    }, [navigateToCase, apiType]);


    const handleViewPrescriptionErrors = (value) => {
        setPrescriptions(prev => ({ ...prev, withErrors: value }))
    }

    const visiblePrescriptions = useMemo(() => {
        if (!allPrescriptions.length) return [];
        return prescriptions.withErrors 
            ? allPrescriptions 
            : allPrescriptions.filter(Rx => !Rx.pharmacyStatus?.toLowerCase().includes('error'));
    }, [allPrescriptions, prescriptions.withErrors]);

    return (
        <Container>
            <SectionHeader title="History" />
            <Tabs
                defaultActiveKey="0"
                items={[{
                    key: "0",
                    label: "Prescriptions",
                    style: { width: '100%' },

                    children: <><Skeleton loading={prescriptionsLoading} active paragraph={{ rows: 6 }}>
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <Tooltip title="Show prescriptions with errors">
                                    <ShowErrorsSwitch
                                        checked={prescriptions.withErrors}
                                        onChange={handleViewPrescriptionErrors}
                                        style={{ width: '150px', marginBottom: '10px', backgroundColor: 'red !important'}}
                                        checkedChildren="Errors included"
                                        unCheckedChildren="Without Errors"
                                    />
                                </Tooltip>
                            </div>
                            {prescriptionsError && (
                                <div style={{ color: '#ff4d4f', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    Error loading prescriptions
                                    <Button size="small" onClick={fetchPrescriptionHistory}>Retry</Button>
                                </div>
                            )}
                            {
                                !prescriptionsLoading && !visiblePrescriptions.length && <Empty />
                            }
                            {
                                !prescriptionsError &&
                                !prescriptionsLoading &&
                                visiblePrescriptions.map((prescription, index) => (
                                    <Prescription
                                        key={prescription._id || index}
                                        prescription={prescription}
                                        loading={prescriptionsLoading}
                                        getDispenseUnitById={getDispenseUnitById}
                                    />
                                ))
                            }
                        </Skeleton></>
                }, {
                    key: "1",
                    label: "Consultations",

                    children: <><Skeleton loading={loading} active paragraph={{ rows: 6 }}>
                            {error && (
                                <div style={{ color: '#ff4d4f', marginBottom: '10px' }}>
                                    Error loading case history
                                </div>
                            )}
                            {!caseHistory?.length && !loading && <Empty />}
                            {
                                caseHistory?.map((request, index) => (
                                    <Request
                                        key={request._id || index}
                                        request={{ ...request, history: caseHistory }}
                                        index={index}
                                        navigateToCase={handleNavigateToCase}
                                    />
                                ))
                            }
                        </Skeleton></>
                }]} />
        </Container>
    );
}
